import { MultiSelect } from '@/components/MultiSelect';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useHoras, useNomina, useSubcontratos } from '@/hooks/useManoObra';
import { useMesAvance, useSemanas } from '@/hooks/useReportes';
import { moPorObraSemanas } from '@/lib/moMes';
import { cn } from '@/lib/utils';
import { type MesAvanceFila, formatearCRC } from '@adelante/shared';
import {
  ArrowDown,
  ArrowUp,
  ArrowsDownUp,
  CaretDown,
  CaretLeft,
  CaretRight,
  CircleNotch,
} from '@phosphor-icons/react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

/**
 * Análisis mensual: árbol Proyecto → Obra → Partida → Sub-partida con el avance
 * del mes (% , m², M.O. y costo teórico según avance). El avance sale de
 * GET /reportes/mes (Σ de los avances semanales); la M.O. se calcula en el
 * front con nómina/horas/subcontratos y solo existe a nivel de obra y arriba
 * (las horas se capturan por obra, no por partida).
 */

type Nivel = 'proyecto' | 'obra' | 'partida' | 'sub';

interface Nodo {
  key: string;
  nivel: Nivel;
  label: string;
  /** Texto secundario (p. ej. cantidad de obras). */
  detalle?: string;
  pct: number;
  m2: number;
  monto: number;
  /** null = no aplica a este nivel (partida / sub). */
  mo: number | null;
  hijos: Nodo[];
}

const MESES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

function etiquetaMes(mes: string): string {
  const [a, m] = mes.split('-');
  return `${MESES[Number(m) - 1]} ${a}`;
}

const fmtNum = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 1 });
const fmtPct = (n: number) => `${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;

export function AnalisisMensualPantalla() {
  const semanasQ = useSemanas();
  const semanas = semanasQ.data?.data ?? [];

  // Meses disponibles (por fecha de inicio de la semana), más reciente primero.
  const meses = useMemo(
    () => [...new Set(semanas.map((s) => s.fecha_inicio.slice(0, 7)))].sort().reverse(),
    [semanas],
  );

  const [mes, setMes] = useState<string | null>(null);
  useEffect(() => {
    if (mes != null || semanas.length === 0) return;
    const abierta = semanas.find((s) => s.estado === 'abierta') ?? semanas[0];
    setMes(abierta?.fecha_inicio.slice(0, 7) ?? null);
  }, [semanas, mes]);

  const idxMes = mes ? meses.indexOf(mes) : -1;
  const irMes = (delta: number) => {
    const m = meses[idxMes + delta];
    if (m) setMes(m);
  };

  const mesQ = useMesAvance(mes);
  const data = mesQ.data?.data ?? null;

  const { data: nominaD } = useNomina();
  const { data: horasD } = useHoras();
  const { data: subcD } = useSubcontratos();

  const moPorObra = useMemo(
    () =>
      moPorObraSemanas(
        (data?.semanas ?? []).map((s) => Number(s.id)),
        nominaD?.data ?? [],
        horasD?.data ?? [],
        subcD?.data ?? [],
      ),
    [data, nominaD, horasD, subcD],
  );

  const [busqueda, setBusqueda] = useState('');
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());

  const [partidaSel, setPartidaSel] = useState<string[]>([]);
  const [subSel, setSubSel] = useState<string[]>([]);
  const filtrando = partidaSel.length > 0 || subSel.length > 0;

  const partidas = useMemo(() => {
    const m = new Map<number, string>();
    for (const f of data?.filas ?? [])
      m.set(f.partida_id, `${f.partida_codigo} — ${f.partida_nombre}`);
    return [...m.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'es', { numeric: true }))
      .map(([id, label]) => ({ value: String(id), label }));
  }, [data]);
  const subsOpciones = useMemo(() => {
    const m = new Map<number, string>();
    for (const f of data?.filas ?? []) {
      if (partidaSel.length === 0 || partidaSel.includes(String(f.partida_id)))
        m.set(f.sub_id, `${f.partida_codigo} · ${f.sub_nombre}`);
    }
    return [...m.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'es', { numeric: true }))
      .map(([id, label]) => ({ value: String(id), label }));
  }, [data, partidaSel]);

  // Con filtro de partida/sub la M.O. (que es por obra) no se puede repartir → se omite.
  const dataFiltrada = useMemo(() => {
    if (!data || !filtrando) return data;
    return {
      ...data,
      filas: data.filas.filter(
        (f) =>
          (partidaSel.length === 0 || partidaSel.includes(String(f.partida_id))) &&
          (subSel.length === 0 || subSel.includes(String(f.sub_id))),
      ),
    };
  }, [data, filtrando, partidaSel, subSel]);

  const { arbol, total } = useMemo(
    () => construirArbol(dataFiltrada, filtrando ? new Map() : moPorObra, busqueda),
    [dataFiltrada, filtrando, moPorObra, busqueda],
  );

  // Al cambiar de mes o búsqueda, arranca con los proyectos abiertos.
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona a mes/búsqueda
  useEffect(() => {
    setAbiertos(new Set(arbol.map((p) => p.key)));
  }, [mes, busqueda, partidaSel, subSel]);

  function alternar(key: string) {
    setAbiertos((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }
  function expandirTodo() {
    const todas = new Set<string>();
    const rec = (n: Nodo) => {
      if (n.hijos.length > 0) todas.add(n.key);
      for (const h of n.hijos) rec(h);
    };
    for (const p of arbol) rec(p);
    setAbiertos(todas);
  }

  // Orden por columna: se aplica entre hermanos de cada nivel (el árbol se conserva).
  const [orden, setOrden] = useState<{ col: ColOrden; dir: 'asc' | 'desc' } | null>(null);
  function ordenarPor(col: ColOrden) {
    setOrden((prev) =>
      prev?.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: col === 'label' ? 'asc' : 'desc' },
    );
  }
  const arbolOrdenado = useMemo(() => {
    if (!orden) return arbol;
    const f = orden.dir === 'asc' ? 1 : -1;
    const cmp = (a: Nodo, b: Nodo) =>
      orden.col === 'label'
        ? f * a.label.localeCompare(b.label, 'es', { numeric: true })
        : f * ((a[orden.col] ?? 0) - (b[orden.col] ?? 0));
    const rec = (n: Nodo): Nodo => ({ ...n, hijos: n.hijos.map(rec).sort(cmp) });
    return arbol.map(rec).sort(cmp);
  }, [arbol, orden]);

  // Filas visibles aplanadas según lo expandido.
  const filas = useMemo(() => {
    const out: { nodo: Nodo; prof: number }[] = [];
    const rec = (n: Nodo, prof: number) => {
      out.push({ nodo: n, prof });
      if (abiertos.has(n.key)) for (const h of n.hijos) rec(h, prof + 1);
    };
    for (const p of arbolOrdenado) rec(p, 0);
    return out;
  }, [arbolOrdenado, abiertos]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">Análisis mensual</h1>
          <p className="text-xs text-muted-foreground">
            Avance del mes por Proyecto → Obra → Partida → Sub-partida (suma de las semanas del
            mes).
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Mes anterior"
            disabled={idxMes < 0 || idxMes >= meses.length - 1}
            onClick={() => irMes(1)}
          >
            <CaretLeft className="h-4 w-4" weight="bold" aria-hidden="true" />
          </Button>
          <Select value={mes ?? ''} onValueChange={setMes}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Seleccionar mes…" />
            </SelectTrigger>
            <SelectContent>
              {meses.map((m) => (
                <SelectItem key={m} value={m}>
                  {etiquetaMes(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Mes siguiente"
            disabled={idxMes <= 0}
            onClick={() => irMes(-1)}
          >
            <CaretRight className="h-4 w-4" weight="bold" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar obra…"
          className="h-8 w-56 text-sm"
        />
        <MultiSelect
          opciones={partidas}
          seleccion={partidaSel}
          onChange={(v) => {
            setPartidaSel(v);
            const ok = new Set(
              (data?.filas ?? [])
                .filter((f) => v.length === 0 || v.includes(String(f.partida_id)))
                .map((f) => String(f.sub_id)),
            );
            setSubSel((prev) => prev.filter((id) => ok.has(id)));
          }}
          todasLabel="Todas las partidas"
        />
        <MultiSelect
          opciones={subsOpciones}
          seleccion={subSel}
          onChange={setSubSel}
          todasLabel="Todas las sub-partidas"
        />
        <Button variant="outline" size="sm" onClick={expandirTodo}>
          Expandir todo
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAbiertos(new Set(arbol.map((p) => p.key)))}
        >
          Solo proyectos
        </Button>
        {data && (
          <span className="text-xs text-muted-foreground">
            {data.semanas.length} semana(s):{' '}
            {data.semanas.map((s) => `S${s.numero_semana}`).join(' · ')}
          </span>
        )}
      </div>

      {data && data.semanas_sin_base.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          ⓘ {data.semanas_sin_base.length} semana(s) del mes no tienen línea base ni cierre previo:
          su avance no es calculable y no suma aquí.
        </div>
      )}

      {mesQ.isLoading && (
        <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Calculando el mes…
        </div>
      )}
      {mesQ.isError && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No se pudo cargar el análisis mensual.
        </p>
      )}

      {data && (
        <Card className="overflow-x-auto">
          <div className="min-w-[820px]">
            <div className="grid grid-cols-[minmax(0,1fr)_110px_100px_130px_150px] border-b border-border bg-zinc-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <ThOrden col="label" orden={orden} onOrdenar={ordenarPor}>
                Proyecto / Obra / Partida / Sub-partida
              </ThOrden>
              <ThOrden col="pct" orden={orden} onOrdenar={ordenarPor} derecha>
                % avance mes
              </ThOrden>
              <ThOrden col="m2" orden={orden} onOrdenar={ordenarPor} derecha>
                m² mes
              </ThOrden>
              <ThOrden col="mo" orden={orden} onOrdenar={ordenarPor} derecha>
                M.O. mes
              </ThOrden>
              <ThOrden col="monto" orden={orden} onOrdenar={ordenarPor} derecha>
                Costo teórico
              </ThOrden>
            </div>
            {filas.map(({ nodo, prof }) => (
              <FilaArbol
                key={nodo.key}
                nodo={nodo}
                prof={prof}
                abierto={abiertos.has(nodo.key)}
                onAlternar={() => alternar(nodo.key)}
              />
            ))}
            {filas.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                {busqueda
                  ? `Ninguna obra coincide con «${busqueda}».`
                  : 'Sin avance registrado en este mes.'}
              </p>
            )}
            {filas.length > 0 && (
              <div className="grid grid-cols-[minmax(0,1fr)_110px_100px_130px_150px] border-t-2 border-border bg-zinc-50 px-3 py-2 text-sm font-semibold tabular-nums">
                <span>TOTAL</span>
                <span className="text-right">{fmtPct(total.pct)}</span>
                <span className="text-right">{fmtNum(total.m2)}</span>
                <span className="text-right font-mono text-xs">
                  {total.mo ? formatearCRC(total.mo) : '—'}
                </span>
                <span className="text-right font-mono text-xs">{formatearCRC(total.monto)}</span>
              </div>
            )}
          </div>
        </Card>
      )}

      <p className="text-[11px] text-muted-foreground">
        % avance: sub-partida = % avanzado en el mes; partida = ponderado por el peso de sus
        sub-partidas; obra y proyecto = costo teórico del mes ÷ presupuesto directo. Costo teórico =
        presupuesto directo × avance del mes. M.O. = nómina prorrateada por horas + subcontratos
        (solo a nivel de obra: las horas no se capturan por partida).
      </p>
    </div>
  );
}

type ColOrden = 'label' | 'pct' | 'm2' | 'mo' | 'monto';

function ThOrden({
  col,
  orden,
  onOrdenar,
  derecha,
  children,
}: {
  col: ColOrden;
  orden: { col: ColOrden; dir: 'asc' | 'desc' } | null;
  onOrdenar: (c: ColOrden) => void;
  derecha?: boolean;
  children: ReactNode;
}) {
  const activo = orden?.col === col;
  return (
    <button
      type="button"
      onClick={() => onOrdenar(col)}
      className={cn(
        'flex items-center gap-1 uppercase tracking-wider hover:text-foreground',
        derecha ? 'justify-end' : 'justify-start',
        activo && 'text-foreground',
      )}
    >
      {children}
      {activo ? (
        orden?.dir === 'asc' ? (
          <ArrowUp className="h-3 w-3" weight="bold" aria-hidden="true" />
        ) : (
          <ArrowDown className="h-3 w-3" weight="bold" aria-hidden="true" />
        )
      ) : (
        <ArrowsDownUp className="h-3 w-3 opacity-30" weight="bold" aria-hidden="true" />
      )}
    </button>
  );
}

function FilaArbol({
  nodo,
  prof,
  abierto,
  onAlternar,
}: {
  nodo: Nodo;
  prof: number;
  abierto: boolean;
  onAlternar: () => void;
}) {
  const tieneHijos = nodo.hijos.length > 0;
  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_110px_100px_130px_150px] items-center border-b border-border px-3 py-1.5 text-sm tabular-nums',
        nodo.nivel === 'proyecto' && 'bg-zinc-100 font-semibold',
        nodo.nivel === 'obra' && 'bg-white font-medium',
        nodo.nivel === 'sub' && 'text-muted-foreground',
      )}
    >
      <div className="flex min-w-0 items-center gap-1" style={{ paddingLeft: prof * 20 }}>
        {tieneHijos ? (
          <button
            type="button"
            onClick={onAlternar}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-zinc-200"
            aria-label={abierto ? 'Contraer' : 'Expandir'}
            aria-expanded={abierto}
          >
            <CaretDown
              className={cn('h-3.5 w-3.5 transition-transform', !abierto && '-rotate-90')}
              weight="bold"
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="h-5 w-5 shrink-0" />
        )}
        <span className={cn('truncate', nodo.nivel === 'obra' && 'font-mono text-xs')}>
          {nodo.label}
        </span>
        {nodo.detalle && (
          <span className="shrink-0 text-[11px] font-normal text-muted-foreground">
            {nodo.detalle}
          </span>
        )}
      </div>
      <span className="text-right">{fmtPct(nodo.pct)}</span>
      <span className="text-right">{fmtNum(nodo.m2)}</span>
      <span className="text-right font-mono text-xs">
        {nodo.mo != null && nodo.mo > 0 ? formatearCRC(nodo.mo) : '—'}
      </span>
      <span className="text-right font-mono text-xs">
        {nodo.monto > 0 ? formatearCRC(nodo.monto) : '—'}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Construcción del árbol
// ---------------------------------------------------------------------------

function construirArbol(
  data: import('@adelante/shared').MesAvanceReporte | null,
  moPorObra: Map<string, number>,
  busqueda: string,
): { arbol: Nodo[]; total: { pct: number; m2: number; mo: number; monto: number } } {
  const vacio = { arbol: [], total: { pct: 0, m2: 0, mo: 0, monto: 0 } };
  if (!data) return vacio;

  const q = busqueda.trim().toLowerCase();
  const cmp = (a: string, b: string) => a.localeCompare(b, 'es', { numeric: true });

  const filasPorObra = new Map<string, MesAvanceFila[]>();
  for (const f of data.filas) {
    const arr = filasPorObra.get(f.obra) ?? [];
    arr.push(f);
    filasPorObra.set(f.obra, arr);
  }

  const porProyecto = new Map<string, Nodo[]>();
  const presupProyecto = new Map<string, number>();

  for (const o of data.obras) {
    if (q && !o.codigo.toLowerCase().includes(q)) continue;
    const filas = filasPorObra.get(o.codigo) ?? [];
    const mo = moPorObra.get(o.codigo) ?? 0;
    if (filas.length === 0 && mo <= 0) continue; // ni avance ni M.O. en el mes

    // Partidas de la obra.
    const partidas = new Map<number, MesAvanceFila[]>();
    for (const f of filas) {
      const arr = partidas.get(f.partida_id) ?? [];
      arr.push(f);
      partidas.set(f.partida_id, arr);
    }
    const nodosPartida: Nodo[] = [...partidas.values()]
      .map((subs) => {
        const p = subs[0] as MesAvanceFila;
        const hijos: Nodo[] = subs
          .sort((a, b) => cmp(a.sub_codigo, b.sub_codigo))
          .map((s) => ({
            key: `s|${o.codigo}|${s.sub_id}`,
            nivel: 'sub' as const,
            label: s.sub_nombre,
            pct: s.pct_mes,
            m2: s.m2_mes,
            monto: s.monto_mes,
            mo: null,
            hijos: [],
          }));
        return {
          key: `p|${o.codigo}|${p.partida_id}`,
          nivel: 'partida' as const,
          label: `${p.partida_codigo} — ${p.partida_nombre}`,
          pct: subs.reduce((a, s) => a + (s.pct_mes * s.peso) / 100, 0),
          m2: subs.reduce((a, s) => a + s.m2_mes, 0),
          monto: subs.reduce((a, s) => a + s.monto_mes, 0),
          mo: null,
          hijos,
        };
      })
      .sort((a, b) => cmp(a.label, b.label));

    const m2 = filas.reduce((a, f) => a + f.m2_mes, 0);
    const monto = filas.reduce((a, f) => a + f.monto_mes, 0);
    const pct =
      o.presupuesto > 0 ? (monto / o.presupuesto) * 100 : o.m2 > 0 ? (m2 / o.m2) * 100 : 0;

    const nodoObra: Nodo = {
      key: `o|${o.codigo}`,
      nivel: 'obra',
      label: o.codigo,
      pct,
      m2,
      monto,
      mo,
      hijos: nodosPartida,
    };
    const arr = porProyecto.get(o.proyecto) ?? [];
    arr.push(nodoObra);
    porProyecto.set(o.proyecto, arr);
    presupProyecto.set(o.proyecto, (presupProyecto.get(o.proyecto) ?? 0) + o.presupuesto);
  }

  const arbol: Nodo[] = [...porProyecto.entries()]
    .sort(([a], [b]) => cmp(a, b))
    .map(([proyecto, obras]) => {
      obras.sort((a, b) => cmp(a.label, b.label));
      const monto = obras.reduce((a, n) => a + n.monto, 0);
      const m2 = obras.reduce((a, n) => a + n.m2, 0);
      const presup = presupProyecto.get(proyecto) ?? 0;
      return {
        key: `pr|${proyecto}`,
        nivel: 'proyecto' as const,
        label: proyecto,
        detalle: `${obras.length} obra${obras.length === 1 ? '' : 's'}`,
        pct: presup > 0 ? (monto / presup) * 100 : 0,
        m2,
        monto,
        mo: obras.reduce((a, n) => a + (n.mo ?? 0), 0),
        hijos: obras,
      };
    });

  const monto = arbol.reduce((a, p) => a + p.monto, 0);
  const presup = [...presupProyecto.values()].reduce((a, v) => a + v, 0);
  return {
    arbol,
    total: {
      pct: presup > 0 ? (monto / presup) * 100 : 0,
      m2: arbol.reduce((a, p) => a + p.m2, 0),
      mo: arbol.reduce((a, p) => a + (p.mo ?? 0), 0),
      monto,
    },
  };
}
